import { useState, useRef } from 'react';
import classNames from 'classnames';

import { getDiscreteValueFromRange } from './volume-slider.helpers';
import { VolumeMute } from '../../../../general/icon/components/volume/volume-mute';
import { VolumeLow } from '../../../../general/icon/components/volume/volume-low';
import { VolumeMedium } from '../../../../general/icon/components/volume/volume-medium';
import { VolumeHigh } from '../../../../general/icon/components/volume/volume-high';
import { useOutsideClick } from '../../../../../global/hooks/use-outside-click';

import styles from './volume-slider.module.scss';

type VolumeSliderProps = {
  volume: number;
  onVolumeChanged: (newVolumeLevel: number) => void;
  onVolumeClicked: () => void;
};

export const VolumeSlider = ({
  onVolumeChanged,
  volume = 0.5,
  onVolumeClicked,
}: VolumeSliderProps) => {
  const controlRef = useRef(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const iconVolumeLevel = getDiscreteValueFromRange(volume, 3, 1);
  useOutsideClick(controlRef, () => {
    setIsOpen(false);
  });

  const onClick = () => {
    setIsOpen(isOpen => !isOpen);
  };

  return (
    <div
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
      onClick={() => onVolumeClicked()}
      ref={controlRef}
      className={classNames(
        styles['volume-slider'],
        isOpen && styles['volume-slider--control-open'],
      )}
    >
      <div
        aria-hidden={!isOpen}
        className={styles['volume-slider__input-container']}
      >
        <div className={styles['volume-slider__input']}>
          <progress max={1} value={volume} />
          <input
            data-hook='volume-slider-range'
            aria-label={polyglot.t('audio_player.volume_slider')}
            type='range'
            value={volume}
            min={0}
            max={1}
            step={0.01}
            onChange={evt => {
              const newValue = parseFloat(evt.target.value);
              onVolumeChanged(newValue);
            }}
          />
        </div>
      </div>
      <div
        onClick={onClick}
        className={styles['volume-slider__icon-container']}
      >
        <div className={styles['volume-slider__icon']}>
          {(() => {
            if (volume === 0) {
              return <VolumeMute />;
            }

            switch (iconVolumeLevel) {
              case 0:
                return <VolumeLow />;
              case 1:
                return <VolumeMedium />;
              case 2:
                return <VolumeHigh />;
            }
          })()}
        </div>
      </div>
    </div>
  );
};
